import { useEffect, useState, useRef } from 'react';
import { Button, message } from 'antd';
// import { DownloadOutlined } from '@ant-design/icons';
import { toPng } from 'html-to-image';
import QRCode from 'qrcode.react';
import { prepareBCOSSUrl } from '@utils/utils';
import { getProjectPosterModulesListAllApi } from '@/services/Selection';
import { organizationsInfoApi } from '@/services/DC/Organization';
import { get } from '@core/request';
import './CasePoster.less';
import './Preview.less';
import config from '@config';

// 成果壁报组件
const CasePoster = ({ fieldInfo, caseInfo }) => {
    const posterRef = useRef(null);
    const [posterConfig, setPosterConfig] = useState(null);
    const [posterFields, setPosterFields] = useState([]);
    const [posterData, setPosterData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [unitInfo, setUnitInfo] = useState(null);

    // 预览面板动态高度计算
    const previewPanelRef = useRef(null);
    const previewHeaderRef = useRef(null);
    const previewFirstItemRef = useRef(null);
    const [twoColumnLayoutHeight, setTwoColumnLayoutHeight] = useState('');

    // 监听 previewHeader 和 previewItemFirst 高度变化，动态计算 twoColumnLayout 高度
    useEffect(() => {
        if (!previewPanelRef.current || !previewFirstItemRef.current) return;

        const resizeObserver = new ResizeObserver(entries => {
            // 获取 previewPanel 的 font-size（基准 1em = 100px）
            const panelElement = previewPanelRef.current;
            const panelFontSize = parseFloat(getComputedStyle(panelElement).fontSize); // 如 100px

            // 获取 previewPanel 总高度（px）
            const panelHeight = panelElement.getBoundingClientRect().height;

            // 获取 previewHeader 高度（px）
            const headerHeight = previewHeaderRef.current ? previewHeaderRef.current.getBoundingClientRect().height : 0;

            // 获取 previewItemFirst 高度（px）
            const firstItemHeight = previewFirstItemRef.current.getBoundingClientRect().height;

            // padding 用 em 单位（0.3em ≈ 30px 当 font-size=100px）
            const paddingEm = 0.4;
            const paddingPx = paddingEm * panelFontSize;

            // 计算剩余高度（px）
            const remainingHeightPx = Math.max(50, panelHeight - headerHeight - firstItemHeight - paddingPx);

            // 转换为 em：剩余高度 / font-size
            const remainingHeightEm = remainingHeightPx / panelFontSize;

            console.log('Remaining Height (em):', remainingHeightEm);

            setTwoColumnLayoutHeight(`${remainingHeightEm}em`);
        });

        // 监听多个元素
        if (previewHeaderRef.current) {
            resizeObserver.observe(previewHeaderRef.current);
        }
        resizeObserver.observe(previewFirstItemRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [posterData]);

    // 获取壁报配置
    const getPosterConfig = async () => {
        if (!caseInfo.projectId) return;

        setLoading(true);
        try {
            const res = await getProjectPosterModulesListAllApi({ projectId: caseInfo.projectId });
            if (res?.status_code === 200 && res.data) {
                const { project, list } = res.data;
                let bgUrl = null;

                // 解析 posterSetting JSON 字符串
                if (project?.posterSetting) {
                    try {
                        const posterSettingObj = JSON.parse(project.posterSetting);
                        bgUrl = posterSettingObj?.bgUrl || null;
                    } catch (e) {
                        console.error('解析 posterSetting 失败:', e);
                    }
                }

                setPosterConfig({
                    posterEnabled: project?.posterEnabled,
                    bgUrl,
                });

                // 设置壁报字段列表
                setPosterFields(list || []);
            }
        } catch (error) {
            console.error('获取壁报配置失败:', error);
        } finally {
            setLoading(false);
        }
    };

    // 处理壁报数据：将 list 与 fieldInfo.poster 进行匹配
    useEffect(() => {
        if (posterFields.length === 0 || !fieldInfo?.poster) {
            setPosterData([]);
            return;
        }

        getUnitInfo(); // 获取单位信息

        // console.log('fieldInfo.poster:', fieldInfo);

        // 用 list 与 poster 中的字段的 id 做比对，相同的才展示
        const matchedData = posterFields
            .map(field => {
                const matchedPoster = fieldInfo.poster.find(poster => poster.id === field.id);
                if (matchedPoster) {
                    return {
                        ...field,
                        ...matchedPoster,
                        // 优先使用 poster 中的值
                        name: matchedPoster.name || field.name,
                        type: matchedPoster.type || field.type,
                        builtIn: matchedPoster.builtIn !== undefined ? matchedPoster.builtIn : field.builtIn,
                    };
                }
                return null;
            })
            .filter(item => item !== null);

        setPosterData(matchedData);
    }, [posterFields, fieldInfo]);

    useEffect(() => {
        getPosterConfig();
    }, [caseInfo.projectId]);

    // 处理下载壁报
    const handleDownload = async () => {
        if (!posterConfig?.bgUrl) {
            message.warning('在“壁报设置”中完成背景上传后，可下载成果壁报');
            return;
        }

        if (!posterRef.current) {
            message.error('壁报元素未找到');
            return;
        }

        setDownloading(true);
        try {
            const posterElement = posterRef.current;

            // html-to-image 通过 SVG foreignObject 由浏览器原生渲染 DOM，
            // 无需任何 onclone 补丁，CSS columns / em 单位 / 富文本换行全部原生还原
            // 先在 pixelRatio:1 下捕获（布局与页面完全一致），再用 canvas 手动升至 4x
            // 避免 pixelRatio>1 时 SVG foreignObject 在缩放上下文里尝试重排版导致布局漂移
            const dataUrl1x = await toPng(posterElement, {
                pixelRatio: 1,
                width: posterElement.offsetWidth,
                height: posterElement.offsetHeight,
                fetchRequestInit: { credentials: 'omit', mode: 'cors' },
                skipFonts: false,
            });

            // 将 1x 截图手动升缩至 4x：像素直接扩展，不经二次布局计算
            const scale = 4;
            const scaledImg = new Image();
            await new Promise((resolve, reject) => {
                scaledImg.onload = resolve;
                scaledImg.onerror = reject;
                scaledImg.src = dataUrl1x;
            });
            const scaledCanvas = document.createElement('canvas');
            scaledCanvas.width = posterElement.offsetWidth * scale;
            scaledCanvas.height = posterElement.offsetHeight * scale;
            const ctx = scaledCanvas.getContext('2d');
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(scaledImg, 0, 0, scaledCanvas.width, scaledCanvas.height);
            const dataUrl = scaledCanvas.toDataURL('image/png', 1.0);

            // 生成图片并下载
            const link = document.createElement('a');
            link.style.display = 'none';
            // 文件名格式：案例ID_实践名称
            const caseId = caseInfo?.id || '';
            const caseName = fieldInfo?.title || '';
            link.download = `${caseId}_${caseName}.png`;
            link.href = dataUrl;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            link.remove();

            message.success('壁报下载成功');
        } catch (error) {
            console.error('下载壁报失败:', error);
            message.error('下载壁报失败，请重试');
        } finally {
            setDownloading(false);
        }
    };

    // 根据fieldInfo中的case_org_id获取单位信息
    const getUnitInfo = async () => {
        if (!fieldInfo.case_org_id) return;

        try {
            const res = await organizationsInfoApi({ id: fieldInfo.case_org_id });
            if (res.status_code === 200 && res.data) {
                setUnitInfo(res.data);
            }
        } catch (error) {
            console.error('获取单位信息失败:', error);
        }
    };

    // 准备logo url，兼容用户中心上传的 upload 打头的 logo url
    const getFileUrl = logo => {
        let url = '';

        if (!!logo && typeof logo === 'string') {
            if (logo.startsWith('upload/')) {
                url = `${config.FILE_PREFIX}/${logo}`;
            } else {
                url = prepareBCOSSUrl(logo);
            }
        }

        return url;
    };

    // 渲染顶部信息栏（logo + 单位信息 + 二维码）
    const renderPreviewHeader = () => {
        // 从 caseInfo 中获取项目名称、单位名称、主题名称
        const hospitalName = caseInfo?.hospitalName || '';
        // 从 fieldInfo 中获取 title 值
        const title = fieldInfo?.title || '';
        const caseNumber = caseInfo?.id || '';

        // 从 poster 字段中获取团队成员（builtIn=1）
        const teamMemberField = posterData.find(f => f.builtIn === 1);
        let teamMembers = '';
        if (teamMemberField) {
            teamMembers = teamMemberField.content || '';
        }

        // 获取单位 logo，若有值则拼接域名
        const logoUrl = unitInfo?.logo ? getFileUrl(unitInfo.logo) : null;

        // 生成二维码 URL
        const qrCodeUrl = caseInfo?.id ? `${config.SELECTION_DOMAIN}/cases/${caseInfo.id}` : '';

        return (
            <div className="previewHeader">
                {/* 左侧 Logo - 仅当有 logo 时展示 */}
                {logoUrl && (
                    <div className="headerLogo">
                        <img src={logoUrl} alt="Logo" className="logoImage" />
                    </div>
                )}

                {/* 中间单位信息 */}
                <div className="headerInfo">
                    <div className="infoTitle">{title}</div>
                    <div className="infoUnit">{hospitalName || ''}</div>
                    <div className="infoTeam">{teamMembers || ''}</div>
                </div>

                {/* 右侧二维码 */}
                <div className="headerQRCode">
                    {qrCodeUrl && <QRCode value={qrCodeUrl} />}
                    {caseNumber && <span className="qrCodeText">#{caseNumber}</span>}
                </div>
            </div>
        );
    };

    // 渲染预览内容
    const renderPreviewItem = (label, value, type = 'text', idx, builtIn) => {
        if (value === undefined || value === null || value === '') return null;

        return (
            <div className={`previewItem ${builtIn === 2 ? 'previewItemFirst' : ''}`}>
                {label && <div className={`previewLabel ${idx ? 'previewLabelNoMarginTop' : ''}`}>{label}</div>}
                <div className="previewValue">
                    {type === 'image' && value ? (
                        <img src={prepareBCOSSUrl(value)} alt={label} className="previewImage" />
                    ) : type === 'textarea' ? (
                        <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.8' }}>{String(value)}</div>
                    ) : type === 'richText' ? (
                        <div dangerouslySetInnerHTML={{ __html: value }} style={{ lineHeight: '1.8' }} />
                    ) : (
                        String(value)
                    )}
                </div>
            </div>
        );
    };

    // 判断是否有背景图
    const hasBgUrl = !!posterConfig?.bgUrl;

    // 判断是否有壁报字段配置
    const hasPosterFields = posterFields.length > 0;

    // 判断是否有壁报数据（list 与 poster 字段匹配后的结果）
    const hasPosterData = posterData.length > 0;

    // 如果没有壁报字段配置或没有壁报数据，不展示壁报展示区
    if (!hasPosterFields || !hasPosterData) {
        return (
            <div className="case-poster-container">
                <div className="poster-empty-state">暂无壁报信息</div>
            </div>
        );
    }

    return (
        <div className="case-poster-container">
            {/* 下载按钮 */}
            <div className="poster-download-bar">
                <Button type="primary" icon={<i className="bi bi-download" />} onClick={handleDownload} disabled={!hasBgUrl} loading={downloading}>
                    下载壁报
                </Button>
            </div>

            {/* 壁报展示区 */}
            <div className="poster-display-wrapper">
                <div
                    ref={posterRef}
                    className="poster-display-area previewContainer"
                    style={{
                        backgroundImage: hasBgUrl ? `url(${posterConfig.bgUrl})` : 'none',
                        backgroundColor: hasBgUrl ? 'transparent' : '#f5f5f5',
                    }}
                >
                    {/* 内容区 */}
                    <div className="poster-content-area">
                        {/* 主题名称 */}
                        <div className="poster-topic-title">{caseInfo?.topicName || ''}</div>

                        {/* 子内容区 */}
                        <div ref={previewPanelRef} className="previewPanel">
                            {/* 顶部信息栏：logo + 单位信息 + 二维码 */}
                            <div ref={previewHeaderRef}>{renderPreviewHeader()}</div>

                            <div className="previewContent">
                                {/* 动态渲染 poster 字段 */}
                                {posterData.map((field, index) => {
                                    const content = field.content || '';
                                    const banner = field.banner || '';

                                    // builtIn=1: 团队成员 - 不在此处显示，在顶部信息栏显示
                                    if (field.builtIn === 1) {
                                        return null;
                                    }

                                    // builtIn=2: 成果摘要 - 通栏展示
                                    if (field.builtIn === 2) {
                                        return (
                                            <div ref={previewFirstItemRef} className="previewFirstItemWrapper" key={`preview-first-${field.id}`}>
                                                {renderPreviewItem(field.name || '成果摘要', content, 'richText', 0, field.builtIn)}
                                            </div>
                                        );
                                    }

                                    return null;
                                })}

                                {/* builtIn=0: 自定义字段 - 统一包裹在两列布局中 */}
                                {posterData.filter(f => f.builtIn === 0).length > 0 && (
                                    <div className="twoColumnLayout" style={{ height: twoColumnLayoutHeight }}>
                                        {posterData.map((field, index) => {
                                            if (field.builtIn !== 0) return null;

                                            const content = field.content || '';
                                            const banner = field.banner || '';
                                            // 查找第一个builtIn=0的字段的index
                                            const firstBuiltIn0Index = posterData.findIndex(f => f.builtIn === 0);

                                            return (
                                                <div key={`preview-poster-${index}-${field.name}`}>
                                                    {renderPreviewItem(field.name, content, field.type === 1 ? 'richText' : 'textarea', index === firstBuiltIn0Index ? 1 : 0)}
                                                    {field.type === 1 && banner && renderPreviewItem('', banner, 'image')}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CasePoster;
